import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { mediaUrl } from '../lib/api';
import { Pin, Loader2, Crown, Sparkles } from 'lucide-react';
import { useLightbox } from '../context/LightboxContext';

const META = {
  prime: { label: 'Server Prime', color: 'text-[var(--gold)]', bg: 'bg-[var(--gold)]', icon: Crown },
  elite: { label: 'Server Elite', color: 'text-blue-400', bg: 'bg-blue-500', icon: Sparkles },
};

function formatDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) + ' · ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function isNew(iso) {
  try {
    return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60) < 48;
  } catch { return false; }
}

export default function NovitaDetail() {
  const { server, id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);
  const meta = META[server] || META.prime;
  const { open: openImage } = useLightbox();

  useEffect(() => {
    api.get(`/contents/${id}`)
      .then(({ data }) => setItem(data))
      .catch(() => setError(true));
  }, [id]);

  return (
    <Layout title={meta.label} showBack backTo={`/novita/${server}`}>
      <div className="max-w-md mx-auto pb-10">
        {!item && !error && (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-20 text-white/50 text-sm">
            Pubblicazione non trovata.
          </div>
        )}

        {item && (
          <>
            {item.image_url && (
              <div className="relative w-full aspect-video bg-[var(--bg-elevated)] overflow-hidden">
                <img
                  src={mediaUrl(item.image_url)}
                  alt=""
                  onClick={() => openImage(mediaUrl(item.image_url), item.text || '')}
                  className="w-full h-full object-cover cursor-zoom-in"
                  data-testid="detail-image"
                />
                {item.is_pinned && (
                  <div className="absolute top-3 left-3 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs text-[var(--gold)]">
                    <Pin className="w-3.5 h-3.5" /> In evidenza
                  </div>
                )}
                {isNew(item.created_at) && (
                  <div className="absolute top-3 right-3 bg-[var(--gold)] text-black text-xs font-bold px-3 py-1.5 rounded-full tracking-widest">
                    NUOVO
                  </div>
                )}
              </div>
            )}

            <div className="px-5 pt-5">
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-2 h-2 rounded-full ${meta.bg}`} />
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">{meta.label}</span>
                <span className="text-[10px] text-white/40 ml-auto">{formatDateTime(item.created_at)}</span>
              </div>

              <div
                data-testid="detail-text"
                className="text-[15px] leading-relaxed text-white whitespace-pre-wrap break-words"
              >
                {item.text}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
